import { useState, useEffect } from 'react';
import { adminStatsAPI, adminUsersAPI } from '../../lib/adminApi';
import { Users, Bot, Megaphone, PhoneCall, BookOpen, Loader2, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#F59E0B', '#06B6D4', '#10B981', '#F59E0B'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-white/10 rounded-lg p-3 shadow-xl text-sm">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color || entry.fill }} className="font-medium">
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

export default function AdminOverviewPage() {
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminStatsAPI.get(), adminUsersAPI.list()])
      .then(([sRes, uRes]) => {
        setStats(sRes.data);
        setRecentUsers(uRes.data.slice(-5).reverse());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  const planData = stats ? [
    { name: 'Free', value: stats.plan_distribution.free },
    { name: 'Starter', value: stats.plan_distribution.starter },
    { name: 'Pro', value: stats.plan_distribution.pro },
    { name: 'Enterprise', value: stats.plan_distribution.enterprise },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-8 max-w-7xl" data-testid="admin-overview-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] text-white">Platform Overview</h1>
        <p className="text-slate-400 mt-1">Monitor your DotCall SaaS platform</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { icon: Users, label: 'Total Users', value: stats?.total_users || 0, color: 'bg-amber-600/20 text-amber-400' },
          { icon: Bot, label: 'AI Agents', value: stats?.total_agents || 0, color: 'bg-cyan-600/20 text-cyan-400' },
          { icon: Megaphone, label: 'Campaigns', value: stats?.total_campaigns || 0, color: 'bg-emerald-600/20 text-emerald-400' },
          { icon: PhoneCall, label: 'Total Calls', value: stats?.total_calls || 0, color: 'bg-amber-600/20 text-amber-400' },
          { icon: BookOpen, label: 'KB Docs', value: stats?.total_kb_docs || 0, color: 'bg-rose-600/20 text-rose-400' },
        ].map((m, i) => (
          <div key={i} className="glass-card rounded-xl p-5 animate-fade-in" style={{ animationDelay: `${i * 60}ms` }} data-testid={`admin-stat-${i}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${m.color}`}>
              <m.icon className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold font-['Outfit'] text-white">{m.value}</div>
            <div className="text-sm text-slate-500 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Plan Distribution */}
        <div className="lg:col-span-4 glass-card rounded-xl p-6" data-testid="admin-plan-chart">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Plan Distribution</h3>
          {planData.length > 0 ? (
            <>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={planData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                      {planData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-2">
                {planData.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-400">{d.name}: {d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-sm">No user data yet</p>
          )}
        </div>

        {/* Recent Users */}
        <div className="lg:col-span-8 glass-card rounded-xl p-6" data-testid="admin-recent-users">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Recent Users</h3>
          {recentUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-2">Name</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-2">Email</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-2">Company</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-2">Plan</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.map((u, i) => (
                    <tr key={u.id || i} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-sm text-white">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{u.email}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{u.company || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${
                          u.plan === 'pro' ? 'bg-amber-600/20 text-amber-400 border-amber-600/30' :
                          u.plan === 'starter' ? 'bg-cyan-600/20 text-cyan-400 border-cyan-600/30' :
                          u.plan === 'enterprise' ? 'bg-amber-600/20 text-amber-400 border-amber-600/30' :
                          'bg-slate-800 text-slate-400 border-slate-700'
                        } capitalize`}>{u.plan || 'free'}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No users yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
